import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CalendarRange,
  Check,
  Flag,
  Pencil,
  Plus,
  RefreshCw,
  Target,
  Trophy,
  X,
} from "lucide-react";
import { seguimientoApi } from "./api";
import { LoadingBlock, ErrorBlock } from "../dashboard/shared/StateBlocks";
import { formatDate, formatDateTime } from "../applications/shared/format";
import { useToast } from "@/app/providers/Toaster";
import { confirmAction } from "@/app/providers/ConfirmDialog";
import { Badge, FieldLabel } from "./ui";
import { SPRINT_STATUS_LABELS, SPRINT_STATUS_TONE } from "./labels";
import type { Sprint } from "./types";

const dayValue = (iso: string | null | undefined) =>
  iso ? new Date(iso).toISOString().slice(0, 10) : "";
const noonUtc = (d: string) => `${d}T12:00:00.000Z`;

export function SprintsPane() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Sprint | "new" | null>(null);

  const sprintsQuery = useQuery({
    queryKey: ["seg", "sprints"],
    queryFn: () => seguimientoApi.sprints(),
  });
  const metricsQuery = useQuery({ queryKey: ["seg", "metrics"], queryFn: seguimientoApi.metrics });
  // remaining (sin terminar) por sprint activo, para el aviso al cerrar.
  const remainingById = new Map(
    (metricsQuery.data?.bySprintActive ?? []).map((s) => [s.sprintId, s.remaining]),
  );

  const sprints = sprintsQuery.data ?? [];
  const activos = sprints.filter((s) => s.status === "activo");
  const cerrados = sprints.filter((s) => s.status === "cerrado");

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm text-fg-muted">
          Iterá el trabajo en sprints. Cerrar un hito congela su performance y devuelve lo no
          terminado al Backlog.
        </p>
        <button
          onClick={() => sprintsQuery.refetch()}
          className="nx-btn-secondary ml-auto"
          disabled={sprintsQuery.isFetching}
        >
          <RefreshCw size={14} className={sprintsQuery.isFetching ? "animate-spin" : ""} />
          Actualizar
        </button>
        <button onClick={() => setEditing("new")} className="nx-btn-primary">
          <Plus size={14} /> Nuevo hito
        </button>
      </div>

      {sprintsQuery.isLoading ? (
        <LoadingBlock label="Cargando sprints…" />
      ) : sprintsQuery.isError ? (
        <ErrorBlock onRetry={() => sprintsQuery.refetch()} />
      ) : sprints.length === 0 ? (
        <p className="text-sm text-fg-subtle">
          Sin sprints todavía. Creá el primer hito para empezar a planificar.
        </p>
      ) : (
        <>
          {activos.length > 0 && (
            <div className="flex flex-col gap-2">
              <p className="nx-eyebrow">Activos</p>
              {activos.map((s) => (
                <SprintCard
                  key={s.id}
                  sprint={s}
                  remaining={remainingById.get(s.id)}
                  onEdit={() => setEditing(s)}
                />
              ))}
            </div>
          )}
          {cerrados.length > 0 && (
            <div className="flex flex-col gap-2 mt-2">
              <p className="nx-eyebrow">Hitos cerrados</p>
              {cerrados.map((s) => (
                <SprintCard key={s.id} sprint={s} />
              ))}
            </div>
          )}
        </>
      )}

      {editing !== null && (
        <SprintDrawer
          sprint={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ["seg", "sprints"] });
            qc.invalidateQueries({ queryKey: ["seg", "metrics"] });
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function SprintCard({
  sprint,
  remaining,
  onEdit,
}: {
  sprint: Sprint;
  remaining?: number;
  onEdit?: () => void;
}) {
  const qc = useQueryClient();
  const { push } = useToast();
  const [open, setOpen] = useState(false);
  const snap = sprint.performanceSnapshot;
  const isActive = sprint.status === "activo";

  const close = useMutation({
    mutationFn: () => seguimientoApi.closeSprint(sprint.id),
    onSuccess: (r) => {
      push({
        kind: "success",
        message: `Hito cerrado: ${r.completed} completada(s), ${r.carriedOver} al backlog.`,
      });
      qc.invalidateQueries({ queryKey: ["seg", "sprints"] });
      qc.invalidateQueries({ queryKey: ["seg", "tasks"] });
      qc.invalidateQueries({ queryKey: ["seg", "metrics"] });
    },
    onError: (err: any) =>
      push({ kind: "error", message: err?.response?.data?.message || "No se pudo cerrar." }),
  });

  return (
    <div className="nx-card p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Target size={15} className="text-accent shrink-0" />
        <span className="font-medium text-fg">{sprint.name}</span>
        <Badge tone={SPRINT_STATUS_TONE[sprint.status]}>{SPRINT_STATUS_LABELS[sprint.status]}</Badge>
        <span className="text-2xs text-fg-subtle inline-flex items-center gap-1">
          <CalendarRange size={12} />
          {formatDate(sprint.startDate)} → {formatDate(sprint.endDate)}
        </span>
        {typeof sprint.taskCount === "number" && (
          <span className="text-2xs text-fg-subtle">· {sprint.taskCount} tarea(s)</span>
        )}
        <div className="ml-auto flex items-center gap-1.5">
          {isActive && onEdit && (
            <button onClick={onEdit} className="nx-btn-ghost p-1.5" aria-label="Editar">
              <Pencil size={14} />
            </button>
          )}
          {isActive && (
            <button
              onClick={async () => {
                const rem =
                  typeof remaining === "number"
                    ? `${remaining} tarea(s) sin terminar volverán al Backlog.`
                    : "Las tareas sin terminar volverán al Backlog.";
                const ok = await confirmAction({
                  title: `Cerrar hito "${sprint.name}"`,
                  message: `${rem} Se congela el performance del sprint (no se puede reabrir).`,
                  confirmLabel: "Cerrar hito",
                  tone: "danger",
                });
                if (ok) close.mutate();
              }}
              className="nx-btn-secondary"
              disabled={close.isPending}
            >
              <Flag size={14} /> Cerrar hito
            </button>
          )}
          {!isActive && snap && (
            <button onClick={() => setOpen((v) => !v)} className="nx-btn-ghost text-xs px-2">
              {open ? "Ocultar" : "Ver resultado"}
            </button>
          )}
        </div>
      </div>

      {sprint.goal && <p className="text-sm text-fg-muted mt-1.5">{sprint.goal}</p>}
      {sprint.segmentTargets &&
        (sprint.segmentTargets.artista ||
          sprint.segmentTargets.productor ||
          sprint.segmentTargets.proveedor) && (
          <p className="text-2xs text-fg-subtle mt-1">
            Meta del hito:{" "}
            {[
              sprint.segmentTargets.artista ? `${sprint.segmentTargets.artista} artistas` : null,
              sprint.segmentTargets.productor
                ? `${sprint.segmentTargets.productor} productores`
                : null,
              sprint.segmentTargets.proveedor
                ? `${sprint.segmentTargets.proveedor} proveedores`
                : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
        )}

      {!isActive && snap && open && (
        <div className="mt-3 border-t border-border pt-3 flex flex-col gap-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Stat label="Velocity" value={`${snap.velocity}`} icon={<Trophy size={13} />} />
            <Stat label="Completado" value={`${snap.percentComplete}%`} />
            <Stat label="Hechas / total" value={`${snap.completedTasks}/${snap.totalTasks}`} />
            <Stat label="Al backlog" value={`${snap.carriedOverTasks}`} />
          </div>
          {(snap.inProgressAtClose > 0 || snap.inReviewAtClose > 0) && (
            <p className="text-2xs text-fg-subtle">
              Al cerrar: {snap.inProgressAtClose} en progreso · {snap.inReviewAtClose} en revisión
              (volvieron al backlog).
            </p>
          )}
          {snap.byArea.length > 0 && (
            <div className="flex flex-col gap-1.5">
              <p className="nx-eyebrow">Por área</p>
              {snap.byArea.map((a) => (
                <div key={String(a.businessAreaId)} className="flex items-center gap-2 text-xs">
                  <span
                    className="inline-block h-2.5 w-2.5 rounded-full shrink-0"
                    style={{ background: a.colorHex ?? "var(--nx-border, #ccc)" }}
                  />
                  <span className="text-fg">{a.areaName}</span>
                  <span className="ml-auto tabular-nums text-fg-muted">
                    {a.completed}/{a.total}
                  </span>
                </div>
              ))}
            </div>
          )}
          {snap.segmentAchieved &&
            (["artista", "productor", "proveedor"] as const).some(
              (seg) => (snap.segmentTargets?.[seg] ?? 0) > 0 || (snap.segmentAchieved?.[seg] ?? 0) > 0,
            ) && (
              <div className="flex flex-col gap-1.5">
                <p className="nx-eyebrow">Objetivo del hito (activaciones)</p>
                {(["artista", "productor", "proveedor"] as const).map((seg) => {
                  const target = snap.segmentTargets?.[seg] ?? 0;
                  const achieved = snap.segmentAchieved?.[seg] ?? 0;
                  if (target === 0 && achieved === 0) return null;
                  const label =
                    seg === "artista" ? "Artistas" : seg === "productor" ? "Productores" : "Proveedores";
                  return (
                    <div key={seg} className="flex items-center gap-2 text-xs">
                      <span className="text-fg">{label}</span>
                      <span className="ml-auto tabular-nums text-fg-muted">
                        {achieved}/{target || "—"}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          {sprint.closedAt && (
            <p className="text-2xs text-fg-subtle">Cerrado el {formatDateTime(sprint.closedAt)}</p>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, icon }: { label: string; value: string; icon?: React.ReactNode }) {
  return (
    <div className="bg-bg-subtle rounded-md p-2">
      <div className="text-2xs text-fg-muted inline-flex items-center gap-1">
        {icon}
        {label}
      </div>
      <div className="text-lg font-semibold text-fg">{value}</div>
    </div>
  );
}

function SprintDrawer({
  sprint,
  onClose,
  onSaved,
}: {
  sprint: Sprint | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { push } = useToast();
  const isNew = sprint === null;
  const [name, setName] = useState(sprint?.name ?? "");
  const [goal, setGoal] = useState(sprint?.goal ?? "");
  const [startDate, setStartDate] = useState(dayValue(sprint?.startDate));
  const [endDate, setEndDate] = useState(dayValue(sprint?.endDate));
  const tgt = sprint?.segmentTargets ?? {};
  const [tArtista, setTArtista] = useState(tgt.artista ? String(tgt.artista) : "");
  const [tProductor, setTProductor] = useState(tgt.productor ? String(tgt.productor) : "");
  const [tProveedor, setTProveedor] = useState(tgt.proveedor ? String(tgt.proveedor) : "");

  useEffect(() => {
    const h = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const save = useMutation({
    mutationFn: () => {
      const toInt = (v: string) => {
        const n = Math.trunc(Number(v));
        return Number.isFinite(n) && n > 0 ? n : 0;
      };
      const body = {
        name: name.trim(),
        goal: goal.trim() || null,
        segmentTargets: {
          artista: toInt(tArtista),
          productor: toInt(tProductor),
          proveedor: toInt(tProveedor),
        },
        startDate: noonUtc(startDate),
        endDate: noonUtc(endDate),
      };
      return isNew
        ? seguimientoApi.createSprint(body)
        : seguimientoApi.updateSprint(sprint!.id, body);
    },
    onSuccess: () => {
      push({ kind: "success", message: isNew ? "Hito creado." : "Hito guardado." });
      onSaved();
    },
    onError: (err: any) =>
      push({ kind: "error", message: err?.response?.data?.message || "No se pudo guardar." }),
  });

  const valid = name.trim() && startDate && endDate && endDate > startDate;

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40 animate-fade-in" onClick={onClose} aria-hidden />
      <aside
        className="fixed top-0 right-0 bottom-0 z-50 w-full sm:w-[460px] bg-bg-card border-l border-border shadow-xl flex flex-col animate-slide-in-right"
        role="dialog"
        aria-modal="true"
      >
        <header className="flex items-start justify-between gap-3 px-5 py-4 border-b border-border">
          <h2 className="nx-h2">{isNew ? "Nuevo hito" : "Editar hito"}</h2>
          <button onClick={onClose} className="nx-btn-ghost p-1.5" aria-label="Cerrar">
            <X size={16} />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-3">
          <div>
            <FieldLabel>Nombre</FieldLabel>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="nx-input"
              placeholder="Sprint 1 · MVP puerta"
              autoFocus
            />
          </div>
          <div>
            <FieldLabel>Objetivo</FieldLabel>
            <textarea
              rows={2}
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              className="nx-input resize-y"
              placeholder="Qué queremos lograr en este hito"
            />
          </div>
          <div>
            <FieldLabel>Metas del hito (opcional)</FieldLabel>
            <p className="text-2xs text-fg-subtle mb-1.5">
              Cuántos sumar en este hito, por segmento. Dejá en 0 lo que no aplique.
            </p>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-2xs text-fg-muted block mb-0.5">Artistas</label>
                <input
                  type="number"
                  min={0}
                  value={tArtista}
                  onChange={(e) => setTArtista(e.target.value)}
                  className="nx-input"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="text-2xs text-fg-muted block mb-0.5">Productores</label>
                <input
                  type="number"
                  min={0}
                  value={tProductor}
                  onChange={(e) => setTProductor(e.target.value)}
                  className="nx-input"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="text-2xs text-fg-muted block mb-0.5">Proveedores</label>
                <input
                  type="number"
                  min={0}
                  value={tProveedor}
                  onChange={(e) => setTProveedor(e.target.value)}
                  className="nx-input"
                  placeholder="0"
                />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <FieldLabel>Inicio</FieldLabel>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="nx-input"
              />
            </div>
            <div>
              <FieldLabel>Fin</FieldLabel>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="nx-input"
              />
            </div>
          </div>
          {startDate && endDate && endDate <= startDate && (
            <p className="text-2xs text-danger">La fecha de fin debe ser posterior a la de inicio.</p>
          )}
        </div>
        <footer className="px-5 py-3 border-t border-border bg-bg-surface flex items-center gap-2 justify-end">
          <button onClick={onClose} className="nx-btn-secondary">
            Cerrar
          </button>
          <button
            onClick={() => valid && save.mutate()}
            disabled={!valid || save.isPending}
            className="nx-btn-primary"
          >
            <Check size={14} /> {isNew ? "Crear" : "Guardar"}
          </button>
        </footer>
      </aside>
    </>
  );
}
